"use client"

import type React from "react"
import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner"
import { useAuth } from "../../../context/AuthContext"
import { useSyncConfiguration } from "../../../hooks/useSyncConfiguration"
import { useLocalStorage } from "../../../hooks/useLocalStorage"

// Components
import LoadingView from "./LoadingView"
import ErrorView from "./ErrorView"
import StepIndicator from "./StepIndicator"
import ConnectorSection from "./ConnectorSection"
import SyncDirectionSection from "./SyncDirectionSection"
import FieldMappingSection from "./FieldMappingSection"

import "./ReviewSyncSetup.css"

const ReviewSyncSetupConnectScreen: React.FC = () => {
  const navigate = useNavigate()
  const { saveConnectorConfig } = useAuth()
  const { setItem } = useLocalStorage()

  const { syncConfig, syncConfigPayload, connectorId, loadingState, error, setStartingSyncState, reloadConfiguration } =
    useSyncConfiguration()

  const handleBack = useCallback(() => {
    // navigate("/dashboard/sync-direction" + syncConfig?.syncOption)
    navigate("/dashboard/sync-direction")
  }, [navigate])

  const handleStartSyncing = useCallback(async () => {
    if (!syncConfig || !syncConfigPayload) return

    try {
      setStartingSyncState(false, true)

      // Save configuration to backend
      const response = await saveConnectorConfig(syncConfigPayload)

      if (response) {
        setItem("connectorFlowStep", "6")
        setItem("syncing-status", "syncing")
        navigate("/dashboard/syncing-progress")
      }
    } catch (error) {
      console.error("Error starting sync:", error)
      // TODO: Add proper error handling/notification
    } finally {
      setStartingSyncState(false, false)
    }
  }, [syncConfig, syncConfigPayload, setStartingSyncState, saveConnectorConfig, setItem, navigate])


    const handleSaveSyncing = useCallback(async () => {
    if (!syncConfig || !syncConfigPayload) return

    try {
      setStartingSyncState(true, false)
      syncConfigPayload.activate = false
      // Save configuration to backend
      const response = await saveConnectorConfig(syncConfigPayload)

      if (response) {
        setItem("connectorFlowStep", "6")
        setItem("syncing-status", "save")
        navigate("/dashboard/syncing-progress")
      }
    } catch (error) {
      console.error("Error starting sync:", error)
      // TODO: Add proper error handling/notification
    } finally {
      setStartingSyncState(false, false)
    }
  }, [syncConfig, syncConfigPayload, setStartingSyncState, saveConnectorConfig, setItem, navigate])

  const handleEditFieldMappings = useCallback(() => {
    navigate(`/dashboard/map-fields?id=${connectorId}`)
  }, [navigate, connectorId])

  const handleEditDirection = useCallback(() => {
    navigate("/dashboard/sync-direction")
  }, [navigate])

  const handleStartOver = useCallback(() => {
    navigate("/dashboard/connectors")
  }, [navigate])

  // Loading state
  if (loadingState.isLoading) {
    return <LoadingView />
  }

  // Error state
  if (error || !syncConfig) {
    return (
      <ErrorView
        title={error ? "Configuration Error" : "Configuration Not Found"}
        description={error || "Unable to load your sync configuration. Please start over."}
        onRetry={error ? reloadConfiguration : undefined}
        onStartOver={handleStartOver}
      />
    )
  }

  return (
    <div className="review-sync-setup">
      <div className="review-header">
        <h1>Review Your Sync Setup</h1>
        <p>Here's a summary of your sync configuration.</p>
        <p>Please review all details before you start syncing.</p>
        <StepIndicator />
      </div>

      <div className="review-sections">
        <ConnectorSection from={syncConfig.connector.from} to={syncConfig.connector.to} />

        <SyncDirectionSection direction={syncConfig.direction} onEdit={handleEditDirection} />

        <FieldMappingSection fieldMappings={syncConfig.fieldMappings} onEdit={handleEditFieldMappings} />
      </div>

      {/* Summary Section */}
      <div className="summary-section">
        <div className="summar-section-header">
            <h3>Configuration Summary</h3>
        </div>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Connector:</span>
            <span className="summary-value">
              {syncConfig.connector.from} → {syncConfig.connector.to}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Direction:</span>
            <span className="summary-value">{syncConfig.direction}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Field Mappings:</span>
            <span className="summary-value">{syncConfig.fieldMappings.length} mappings</span>
          </div>
        </div>
      </div>

      {/* Warning Section */}
      <div className="warning-section">
        <div className="warning-icon">⚠️</div>
        <div className="warning-content">
          <p>
            <strong>Make sure required fields are mapped.</strong>
          </p>
          <p>You'll be able to monitor sync progress and logs from the Dashboard.</p>
          <p>✓ You can change sync settings anytime.</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="actions-section">
        <button className="back-button" onClick={handleBack}>
          Back
        </button>
        <button className="start-syncing-button" onClick={handleSaveSyncing} disabled={loadingState.isStartingSync}>
          {loadingState.isStartingSync ? (
            <>
              <LoadingSpinner size="small" />
              Starting Sync...
            </>
          ) : (
            "Save"
          )}
        </button>
        <button className="start-syncing-button" onClick={handleStartSyncing} disabled={loadingState.isStartingSync}>
          {loadingState.isStartingSynAndSave ? (
            <>
              <LoadingSpinner size="small" />
              Starting Sync...
            </>
          ) : (
            "Save and Activate"
          )}
        </button>
      </div>
    </div>
  )
}

export default ReviewSyncSetupConnectScreen
